/**
 * Manage Users Screen
 * Manage shop users (ADMIN/STAFF roles)
 * Load existing users, create new users, delete users, reset passwords
 * Uses shop theme for UI
 */

import React, { useState, useEffect } from 'react';
import { View, ScrollView, StyleSheet, TouchableOpacity, Modal, Alert, ActivityIndicator } from 'react-native';
import { useAuthTokens, useCurrentShop, getConfig, useThemeColors, useThemeAccent, configurationService, type Country } from '@app/core';
import { Button, Input, Card, Text } from '@app/design-system';
import { colors } from '@app/design-system';
import { ShopHeader } from '@modules/shop-shell';

interface ManageUserScreenProps {
  onBack?: () => void;
  onHome?: () => void;
  onManageShop?: () => void;
  onManageCatalog?: () => void;
  onManageOrders?: () => void;
  onWebsiteBuilder?: () => void;
  onLogout?: () => void;
}

interface User {
  id: string;
  email?: string;
  mobileCountryCode?: string;
  mobileNumber?: string;
  role: 'ADMIN' | 'STAFF';
  status: 'ACTIVE' | 'SUSPENDED' | 'DELETED';
  createdAt?: string;
}

interface CreateUserForm {
  email: string;
  mobileCountryCode: string;
  mobileNumber: string;
  password: string;
  confirmPassword: string;
  role: 'ADMIN' | 'STAFF';
}

interface ResetPasswordForm {
  password: string;
  confirmPassword: string;
}

// API Service for users
const userService = {
  getUsers: async (accessToken: string) => {
    const config = getConfig();
    console.log('🔑 [UserService] Using token:', accessToken ? `${accessToken.substring(0, 20)}...` : 'MISSING');

    const response = await fetch(`${config.apiBaseUrl}/api/auth/users`, {
      headers: {
        'Authorization': `Bearer ${accessToken}`,
      },
    });

    console.log('📡 [UserService] Response status:', response.status, response.statusText);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: response.statusText }));
      console.error('❌ [UserService] Error response:', errorData);
      throw new Error(errorData.message || 'Failed to load users');
    }

    return response.json();
  },

  createUser: async (data: Omit<CreateUserForm, 'confirmPassword'>, accessToken: string) => {
    const config = getConfig();
    console.log('🔑 [UserService] Creating user with token:', accessToken ? `${accessToken.substring(0, 20)}...` : 'MISSING');
    console.log('📝 [UserService] User data:', { email: data.email, role: data.role, mobile: data.mobileNumber });

    const response = await fetch(`${config.apiBaseUrl}/api/auth/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        password: data.password,
        confirmPassword: data.password,
        role: data.role,
        email: data.email,
        mobileCountryCode: data.mobileCountryCode,
        mobileNumber: data.mobileNumber,
      }),
    });

    console.log('📡 [UserService] Register response:', response.status, response.statusText);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: response.statusText }));
      console.error('❌ [UserService] Create user error:', errorData);
      throw new Error(errorData.message || 'Failed to create user');
    }

    return response.json();
  },

  deleteUser: async (userId: string, accessToken: string) => {
    const config = getConfig();
    const endpoint = `${config.apiBaseUrl}/api/auth/users/${userId}`;

    console.log('═══════════════════════════════════════════════════════════');
    console.log('🗑️ [UserService] DELETE USER - START');
    console.log('═══════════════════════════════════════════════════════════');
    console.log('📍 Endpoint:', endpoint);
    console.log('🔑 Access Token:', accessToken ? `${accessToken.substring(0, 40)}...` : '❌ MISSING');
    console.log('👤 User ID:', userId);
    console.log('⏰ Timestamp:', new Date().toISOString());

    const requestHeaders = {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    };

    console.log('📨 Request Headers:', {
      'Authorization': `Bearer ${accessToken.substring(0, 30)}...`,
      'Content-Type': 'application/json',
    });

    try {
      console.log('🌐 Sending DELETE request to:', endpoint);
      console.log('─────────────────────────────────────────────────────────');

      const startTime = performance.now();

      const response = await fetch(endpoint, {
        method: 'DELETE',
        headers: requestHeaders,
      });

      const endTime = performance.now();
      const duration = (endTime - startTime).toFixed(2);

      console.log('─────────────────────────────────────────────────────────');
      console.log('📥 RESPONSE RECEIVED');
      console.log('⏱️ Duration:', `${duration}ms`);
      console.log('✓ Status Code:', response.status);
      console.log('✓ Status Text:', response.statusText);
      console.log('✓ Content-Type:', response.headers.get('content-type'));
      console.log('✓ Content-Length:', response.headers.get('content-length') || 'N/A');

      // Log all response headers
      console.log('✓ All Response Headers:', {
        'content-type': response.headers.get('content-type'),
        'content-length': response.headers.get('content-length'),
        'server': response.headers.get('server'),
        'date': response.headers.get('date'),
      });

      console.log('✓ Response OK:', response.ok);

      if (!response.ok) {
        console.log('❌ Response NOT OK - Reading error body');
        const errorText = await response.text();

        console.log('❌ Error Response Body:', errorText);
        console.log('❌ Error Length:', errorText.length);

        let errorData: any = {};
        try {
          errorData = JSON.parse(errorText);
          console.log('✓ Parsed Error JSON:', errorData);
        } catch (parseError) {
          console.log('⚠️ Could not parse error as JSON - treating as plain text');
          errorData = { message: errorText || response.statusText };
        }

        throw new Error(errorData.message || errorData.details || `Failed to delete user: ${response.statusText}`);
      }

      // Handle both JSON response and empty response
      const contentType = response.headers.get('content-type');
      console.log('📦 Processing Response Body');
      console.log('  Content-Type:', contentType);

      let result: any;

      if (contentType && contentType.includes('application/json')) {
        const responseText = await response.text();
        console.log('  Response Text (JSON):', responseText);

        if (responseText) {
          result = JSON.parse(responseText);
          console.log('✅ Parsed JSON Response:', result);
        } else {
          console.log('⚠️ Empty response body');
          result = { success: true, message: 'User deleted successfully' };
        }
      } else {
        const responseText = await response.text();
        console.log('  Response Text (Plain):', responseText);
        result = { success: true, message: responseText || 'User deleted successfully' };
      }

      console.log('═══════════════════════════════════════════════════════════');
      console.log('✅ DELETE USER - SUCCESS');
      console.log('═══════════════════════════════════════════════════════════');
      console.log('Result:', result);

      return result;
    } catch (error) {
      console.log('═══════════════════════════════════════════════════════════');
      console.log('❌ DELETE USER - FAILED');
      console.log('═══════════════════════════════════════════════════════════');
      console.error('Error Type:', error instanceof Error ? error.constructor.name : typeof error);
      console.error('Error Message:', error instanceof Error ? error.message : String(error));
      console.error('Full Error:', error);

      if (error instanceof Error && error.stack) {
        console.error('Stack Trace:', error.stack);
      }

      throw error;
    }
  },

  resetPassword: async (userId: string, newPassword: string, accessToken: string) => {
    const config = getConfig();
    const response = await fetch(`${config.apiBaseUrl}/api/auth/reset-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        userId: userId,
        newPassword: newPassword,
        confirmNewPassword: newPassword,
      }),
    });
    if (!response.ok) throw new Error('Failed to reset password');
    return response.json();
  },
};

export const ManageUserScreen: React.FC<ManageUserScreenProps> = ({
  onBack,
  onHome,
  onManageShop,
  onManageCatalog,
  onManageOrders,
  onWebsiteBuilder,
  onLogout,
}) => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddUser, setShowAddUser] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [countries, setCountries] = useState<Country[]>([]);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [currentUserRole, setCurrentUserRole] = useState<'ADMIN' | 'STAFF' | null>(null);

  // Form states
  const [createForm, setCreateForm] = useState<CreateUserForm>({
    email: '',
    mobileCountryCode: '91',
    mobileNumber: '',
    password: '',
    confirmPassword: '',
    role: 'STAFF',
  });

  const [resetPasswordForm, setResetPasswordForm] = useState<ResetPasswordForm>({
    password: '',
    confirmPassword: '',
  });

  const [createErrors, setCreateErrors] = useState<Partial<CreateUserForm>>({});
  const [resetPasswordErrors, setResetPasswordErrors] = useState<Partial<ResetPasswordForm>>({});

  const shop = useCurrentShop();
  const tokens = useAuthTokens();
  const themeColors = useThemeColors();
  const accentColor = useThemeAccent();

  // STAFF user limit
  const MAX_STAFF_USERS = 3;
  const staffCount = users.filter(u => u.role === 'STAFF' && u.status !== 'DELETED').length;
  const canAddStaff = staffCount < MAX_STAFF_USERS;

  // Load users on mount
  useEffect(() => {
    if (tokens?.oauthToken) {
      loadUsers();
    }
  }, [tokens?.oauthToken]);  // ✅ Correct dependency

  // Load configuration data (countries)
  useEffect(() => {
    loadConfigurationData();
  }, []);

  const loadConfigurationData = async () => {
    try {
      setLoadingConfig(true);
      console.log('📋 [ManageUser] Loading configuration data...');

      const countriesData = await configurationService.getCountries({ activeOnly: true });
      console.log('✅ [ManageUser] Configuration data loaded:', {
        countriesCount: countriesData.length,
      });

      setCountries(countriesData);

      // Set default country code if not set and countries are available
      if (!createForm.mobileCountryCode && countriesData.length > 0) {
        const defaultCountry = countriesData.find(c => c.mobileCountryCode === '91') || countriesData[0];
        setCreateForm((prev) => ({
          ...prev,
          mobileCountryCode: defaultCountry.mobileCountryCode,
        }));
      }
    } catch (error) {
      console.error('❌ [ManageUser] Failed to load configuration data:', error);
      Alert.alert(
        'Configuration Error',
        'Failed to load countries. Some features may not work properly.'
      );
      // Set empty arrays on error
      setCountries([]);
    } finally {
      setLoadingConfig(false);
    }
  };

  const loadUsers = async () => {
    try {
      setLoading(true);
      if (!tokens?.oauthToken) {
        throw new Error('Access token not found');
      }

      console.log('📡 Calling GET /api/auth/users...');
      const data = await userService.getUsers(tokens.oauthToken);
      console.log('✅ Users loaded:', data.users?.length || 0, data);
      setUsers(data.users || []);
      setCurrentUserRole('ADMIN'); // If successful, user is ADMIN
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load users';
      console.error('❌ Load users error:', errorMessage, error);

      // Check if it's a permission error indicating STAFF role
      if (errorMessage.includes('Permission denied') || errorMessage.includes('Only ADMIN users')) {
        console.log('🔒 [ManageUser] User has STAFF role - limited access');
        setCurrentUserRole('STAFF');
        setUsers([]); // STAFF users can't see user list
        // Don't show error alert for STAFF users, just show appropriate UI
      } else {
        // For other errors, show alert
        Alert.alert('Error', errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const validateCreateForm = (): boolean => {
    const newErrors: Partial<CreateUserForm> = {};

    if (!createForm.email.trim() && !createForm.mobileNumber.trim()) {
      newErrors.email = 'Email or mobile number is required';
    }

    if (createForm.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(createForm.email)) {
      newErrors.email = 'Invalid email format';
    }

    // Mobile number validation - must be exactly 10 digits if provided
    if (createForm.mobileNumber.trim()) {
      const mobileNumberOnly = createForm.mobileNumber.replace(/\D/g, ''); // Remove non-digits
      if (mobileNumberOnly.length !== 10) {
        newErrors.mobileNumber = 'Mobile number must be exactly 10 digits';
      } else if (!/^[6-9]\d{9}$/.test(mobileNumberOnly)) {
        newErrors.mobileNumber = 'Invalid mobile number format (must start with 6-9)';
      }
    }

    if (!createForm.password.trim()) {
      newErrors.password = 'Password is required';
    } else if (createForm.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    if (createForm.password !== createForm.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setCreateErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateResetPasswordForm = (): boolean => {
    const newErrors: Partial<ResetPasswordForm> = {};

    if (!resetPasswordForm.password.trim()) {
      newErrors.password = 'Password is required';
    } else if (resetPasswordForm.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    if (resetPasswordForm.password !== resetPasswordForm.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setResetPasswordErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCreateUser = async () => {
    if (!validateCreateForm() || !tokens?.oauthToken) {
      return;
    }

    setSaving(true);

    try {
      const response = await userService.createUser(createForm, tokens.oauthToken);

      // Transform response to match User interface
      const newUser: User = {
        id: response.userId || response.id,
        email: response.email,
        mobileCountryCode: response.mobileCountryCode,
        mobileNumber: response.mobileNumber,
        role: response.role,
        status: 'ACTIVE', // New users are active by default
        createdAt: response.createdAt,
      };

      setUsers((prev) => [...prev, newUser]);
      setCreateForm({
        email: '',
        mobileCountryCode: '91',
        mobileNumber: '',
        password: '',
        confirmPassword: '',
        role: 'STAFF',
      });
      setShowAddUser(false);

      Alert.alert('Success', 'User created successfully!');
      console.log('✅ User created:', newUser);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create user';
      Alert.alert('Error', errorMessage);
      console.error('Create user error:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUser = async (userId: string, userIdentifier: string, userRole: 'ADMIN' | 'STAFF') => {
    console.log('═══════════════════════════════════════════════════════════');
    console.log('🗑️ [ManageUser] HANDLE DELETE USER - START');
    console.log('═══════════════════════════════════════════════════════════');
    console.log('👤 User ID:', userId);
    console.log('📧 User Identifier:', userIdentifier);
    console.log('🎭 User Role:', userRole);
    console.log('🔐 Token Available:', !!tokens?.oauthToken);
    console.log('⏰ Timestamp:', new Date().toISOString());

    // Prevent deletion of ADMIN users
    if (userRole === 'ADMIN') {
      console.log('⚠️ [ManageUser] Cannot delete ADMIN user - prevented');
      console.log('⚠️ [ManageUser] Showing ADMIN deletion prevention alert');

      // Use browser confirm for web compatibility
      if (typeof window !== 'undefined' && window.alert) {
        window.alert('ADMIN users cannot be deleted. Only STAFF users can be removed.');
        console.log('✅ [ManageUser] Alert shown via window.alert');
      } else {
        Alert.alert(
          'Cannot Delete',
          'ADMIN users cannot be deleted. Only STAFF users can be removed.',
          [{ text: 'OK' }]
        );
        console.log('✅ [ManageUser] Alert shown via Alert.alert');
      }
      return;
    }

    console.log('📋 [ManageUser] Preparing to show confirmation dialog...');

    // Use browser confirm for web compatibility
    let confirmed = false;
    if (typeof window !== 'undefined' && window.confirm) {
      console.log('🌐 [ManageUser] Using window.confirm for web');
      confirmed = window.confirm(`Are you sure you want to delete ${userIdentifier}?\n\nThis action cannot be undone.`);
      console.log('🔔 [ManageUser] User confirmation result:', confirmed);
    } else {
      console.log('📱 [ManageUser] Using Alert.alert for native');
      // Fallback to Alert.alert for native (though we're on web)
      Alert.alert(
        'Delete User',
        `Are you sure you want to delete ${userIdentifier}?`,
        [
          {
            text: 'Cancel',
            style: 'cancel',
            onPress: () => {
              console.log('🚫 [ManageUser] User deletion CANCELLED by user');
              confirmed = false;
            }
          },
          {
            text: 'Delete',
            onPress: () => {
              console.log('✅ [ManageUser] User CONFIRMED deletion via Alert');
              confirmed = true;
            },
            style: 'destructive',
          },
        ]
      );
      console.log('📋 [ManageUser] Alert.alert triggered, waiting for response');
      return; // Exit here for Alert.alert since it's async via callbacks
    }

    if (!confirmed) {
      console.log('🚫 [ManageUser] User cancelled deletion');
      console.log('═══════════════════════════════════════════════════════════');
      console.log('🚫 [ManageUser] HANDLE DELETE USER - CANCELLED');
      console.log('═══════════════════════════════════════════════════════════');
      return;
    }

    // Proceed with deletion
    console.log('─────────────────────────────────────────────────────────');
    console.log('🗑️ [ManageUser] User CONFIRMED deletion - proceeding');
    console.log('─────────────────────────────────────────────────────────');

    setSaving(true);
    console.log('🔄 [ManageUser] Saving state set to true');

    try {
      if (!tokens?.oauthToken) {
        console.error('❌ [ManageUser] FATAL: Access token not found');
        throw new Error('Access token not found');
      }

      console.log('📋 [ManageUser] Pre-deletion state:');
      console.log('  - Users count:', users.length);
      console.log('  - Token length:', tokens.oauthToken.length);
      console.log('  - Token prefix:', `${tokens.oauthToken.substring(0, 20)}...`);

      console.log('─────────────────────────────────────────────────────────');
      console.log('🌐 [ManageUser] Calling userService.deleteUser()...');
      console.log('  Endpoint: DELETE /api/auth/users/' + userId);
      console.log('─────────────────────────────────────────────────────────');

      const deleteStartTime = performance.now();
      console.log('⏱️ [ManageUser] Delete API call START at:', new Date().toISOString());

      const deleteResult = await userService.deleteUser(userId, tokens.oauthToken);

      const deleteEndTime = performance.now();
      console.log('⏱️ [ManageUser] Delete API call END at:', new Date().toISOString());

      console.log('─────────────────────────────────────────────────────────');
      console.log('✅ [ManageUser] Delete API call completed successfully');
      console.log('⏱️ API call duration:', `${(deleteEndTime - deleteStartTime).toFixed(2)}ms`);
      console.log('📊 Delete result:', deleteResult);

      // Remove user from local state
      console.log('🔄 [ManageUser] Filtering users array...');
      const updatedUsers = users.filter((u) => u.id !== userId);

      console.log('📋 [ManageUser] Post-deletion state:');
      console.log('  - Users count before:', users.length);
      console.log('  - Users count after:', updatedUsers.length);
      console.log('  - Removed user ID:', userId);
      console.log('  - Users array before:', users.map(u => ({ id: u.id, email: u.email })));
      console.log('  - Users array after:', updatedUsers.map(u => ({ id: u.id, email: u.email })));

      console.log('🔄 [ManageUser] Calling setUsers() to update state...');
      setUsers(updatedUsers);
      console.log('✅ [ManageUser] setUsers() called successfully');

      console.log('✅ [ManageUser] UI state updated');
      console.log('═══════════════════════════════════════════════════════════');
      console.log('✅ [ManageUser] HANDLE DELETE USER - SUCCESS');
      console.log('═══════════════════════════════════════════════════════════');

      // Show success message
      if (typeof window !== 'undefined' && window.alert) {
        window.alert('User deleted successfully!');
        console.log('✅ [ManageUser] Success alert shown via window.alert');
      } else {
        Alert.alert('Success', 'User deleted successfully!');
        console.log('✅ [ManageUser] Success alert shown via Alert.alert');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete user';
      console.log('═══════════════════════════════════════════════════════════');
      console.log('❌ [ManageUser] HANDLE DELETE USER - ERROR');
      console.log('═══════════════════════════════════════════════════════════');
      console.error('❌ [ManageUser] Error Type:', error instanceof Error ? error.constructor.name : typeof error);
      console.error('❌ [ManageUser] Error Message:', errorMessage);
      console.error('❌ [ManageUser] Full Error:', error);

      if (error instanceof Error && error.stack) {
        console.error('❌ [ManageUser] Error Stack:', error.stack);
      }

      // Provide more specific error messages
      let userMessage = errorMessage;
      if (errorMessage.includes('Permission denied') || errorMessage.includes('ADMIN')) {
        userMessage = 'You do not have permission to delete users. Only ADMIN users can delete staff members.';
      } else if (errorMessage.includes('not found') || errorMessage.includes('404')) {
        userMessage = 'User not found. The user may have already been deleted.';
      } else if (errorMessage.includes('token') || errorMessage.includes('auth')) {
        userMessage = 'Authentication error. Please log in again.';
      }

      console.error('📍 User-friendly message:', userMessage);

      // Show error message
      if (typeof window !== 'undefined' && window.alert) {
        window.alert(`Error: ${userMessage}`);
        console.log('❌ [ManageUser] Error alert shown via window.alert');
      } else {
        Alert.alert('Error', userMessage);
        console.log('❌ [ManageUser] Error alert shown via Alert.alert');
      }
    } finally {
      setSaving(false);
      console.log('🔄 [ManageUser] Saving state reset to false');
      console.log('═══════════════════════════════════════════════════════════');
      console.log('🏁 [ManageUser] HANDLE DELETE USER - COMPLETE (finally block)');
      console.log('═══════════════════════════════════════════════════════════');
    }
  };

  const handleResetPassword = async (userId: string) => {
    if (!validateResetPasswordForm() || !tokens?.oauthToken) {
      return;
    }

    setSaving(true);

    try {
      await userService.resetPassword(userId, resetPasswordForm.password, tokens.oauthToken);

      setResetPasswordForm({
        password: '',
        confirmPassword: '',
      });
      setShowResetPassword(null);

      Alert.alert('Success', 'Password reset successfully!');
      console.log('✅ Password reset');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to reset password';
      Alert.alert('Error', errorMessage);
      console.error('Reset password error:', error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <View style={[styles.loadingContainer, { backgroundColor: themeColors.secondary }]}>
        <ActivityIndicator size="large" color={accentColor} />
        <Text variant="body" color="secondary" style={styles.loadingText}>
          Loading users...
        </Text>
      </View>
    );
  }

  return (
    <>
      {/* Shop Header with Navigation Mode */}
      <ShopHeader
        mode="navigation"
        title="Manage Users"
        onHome={onHome || onBack}
        onManageShop={onManageShop}
        onManageUser={onBack}
        onManageCatalog={onManageCatalog}
        onManageOrders={onManageOrders}
        onWebsiteBuilder={onWebsiteBuilder}
        onLogout={onLogout}
      />

      <ScrollView contentContainerStyle={[styles.container, { backgroundColor: themeColors.secondary }]} keyboardShouldPersistTaps="handled">

        {/* Show different content based on user role */}
        {currentUserRole === 'STAFF' ? (
          // STAFF users see restricted access message
          <Card style={styles.restrictedCard}>
            <Text variant="h3" bold style={styles.restrictedTitle}>
              🔒 Access Restricted
            </Text>
            <Text variant="body" color="secondary" style={styles.restrictedText}>
              Only ADMIN users can view and manage shop users.
            </Text>
            <Text variant="caption" color="secondary" style={styles.restrictedSubtext}>
              Your role: <Text style={{ fontWeight: 'bold' }}>STAFF</Text>
            </Text>
            <Text variant="caption" color="secondary" style={styles.restrictedSubtext}>
              Contact your shop admin to manage users.
            </Text>
          </Card>
        ) : currentUserRole === 'ADMIN' ? (
          // ADMIN users see full functionality
          <>
            {/* STAFF User Limit Notification */}
            <Card style={styles.notificationCard}>
              <View style={styles.notificationContent}>
                <Text variant="body" style={styles.notificationIcon}>ℹ️</Text>
                <View style={styles.notificationTextContainer}>
                  <Text variant="body" style={styles.notificationText}>
                    You can create up to <Text style={styles.notificationBold}>3 STAFF users</Text> to manage your shop catalogs.
                  </Text>
                  <Text variant="caption" color="secondary" style={styles.notificationSubtext}>
                    Current STAFF users: <Text style={styles.notificationBold}>{staffCount}/{MAX_STAFF_USERS}</Text>
                  </Text>
                </View>
              </View>
            </Card>

            {/* Header with Add Button - Only show if under limit */}
            {canAddStaff && (
              <View style={styles.header}>
                <Button
                  onPress={() => setShowAddUser(true)}
                  style={styles.addButton}
                  themeColor={accentColor}
                >
                  + Add User
                </Button>
              </View>
            )}

            {/* Users List */}
            {users.length === 0 ? (
              <Card style={styles.emptyCard}>
                <Text variant="h3" bold style={styles.emptyTitle}>
                  No Users
                </Text>
                <Text variant="body" color="secondary" style={styles.emptyText}>
                  Create your first user to get started
                </Text>
              </Card>
            ) : (
              <View style={styles.usersList}>
                {users.map((user) => (
                  <Card key={user.id} style={styles.userCard}>
                    {/* User Info */}
                    <View style={styles.userInfo}>
                      <View style={styles.userDetails}>
                        <Text variant="body" bold style={styles.userIdentifier}>
                          {user.email || user.mobileNumber}
                        </Text>
                        <View style={styles.userMeta}>
                          <Text variant="caption" color="secondary">
                            Role: {user.role}
                          </Text>
                          <Text variant="caption" color="secondary" style={styles.metaItem}>
                            Status: {user.status}
                          </Text>
                        </View>
                      </View>
                    </View>

                    {/* Action Buttons */}
                    <View style={styles.actions}>
                      <Button
                        variant="outline"
                        onPress={() => {
                          console.log('🔑 [ManageUser] Reset password clicked for user:', user.id);
                          setShowResetPassword(user.id);
                        }}
                        style={styles.actionButton}
                        themeColor={accentColor}
                      >
                        🔑 Reset Password
                      </Button>

                      {/* Only show Delete button for STAFF users */}
                      {user.role === 'STAFF' && (
                        <Button
                          variant="outline"
                          onPress={() => {
                            console.log('🗑️ [ManageUser] Delete button clicked for user:', {
                              id: user.id,
                              email: user.email,
                              mobile: user.mobileNumber,
                              role: user.role,
                            });
                            handleDeleteUser(user.id, user.email || user.mobileNumber || 'User', user.role);
                          }}
                          style={styles.actionButton}
                          themeColor={accentColor}
                          disabled={saving}
                        >
                          🗑️ Delete
                        </Button>
                      )}
                    </View>
                  </Card>
                ))}
              </View>
            )}
          </>
        ) : (
          // Loading or unknown role
          <Card style={styles.emptyCard}>
            <Text variant="body" color="secondary" style={styles.emptyText}>
              Checking permissions...
            </Text>
          </Card>
        )}
      </ScrollView>

      {/* Add User Modal - Only show for ADMIN users */}
      {currentUserRole === 'ADMIN' && (
        <Modal
          visible={showAddUser}
          transparent
          animationType="slide"
          onRequestClose={() => setShowAddUser(false)}
        >
          <View style={[styles.modalContainer, { backgroundColor: 'rgba(0, 0, 0, 0.5)' }]}>
            <ScrollView contentContainerStyle={[styles.modalContent, { backgroundColor: themeColors.secondary }]} keyboardShouldPersistTaps="handled">
              <View style={[styles.modalHeader, { backgroundColor: themeColors.primary }]}>
                <Text variant="h3" bold style={{ color: colors.white }}>
                  ➕ Add New User
                </Text>
                <TouchableOpacity onPress={() => setShowAddUser(false)}>
                  <Text style={[styles.closeButton, { color: colors.white }]}>✕</Text>
                </TouchableOpacity>
              </View>

              <Card style={styles.formCard}>
                {/* ...existing form content... */}
                <Input
                  label="Email"
                  placeholder="user@example.com"
                  value={createForm.email}
                  onChangeText={(value) => {
                    setCreateForm((prev) => ({ ...prev, email: value }));
                    if (createErrors.email) setCreateErrors((prev) => ({ ...prev, email: undefined }));
                  }}
                  error={createErrors.email}
                  testID="create-user-email-input"
                />

                <View style={styles.mobileInputGroup}>
                  <View style={styles.countryCodeInput}>
                    <Text style={styles.label}>Mobile Country Code</Text>
                    <View style={styles.pickerContainer}>
                      <select
                        value={createForm.mobileCountryCode}
                        onChange={(e) => {
                          console.log('🔄 [ManageUser] Country code changed:', e.target.value);
                          setCreateForm((prev) => ({ ...prev, mobileCountryCode: e.target.value }));
                        }}
                        style={styles.picker}
                      >
                        <option value="">Select Country</option>
                        {Array.isArray(countries) && countries.length > 0 ? (
                          countries.map((country) => {
                            console.log('🌍 [ManageUser] Rendering country option:', country);
                            return (
                              <option key={country.id} value={country.mobileCountryCode}>
                                {country.countryName} (+{country.mobileCountryCode})
                              </option>
                            );
                          })
                        ) : (
                          <option value="" disabled>Loading countries...</option>
                        )}
                      </select>
                    </View>
                    {loadingConfig && (
                      <Text variant="caption" color="secondary" style={styles.helperText}>
                        Loading countries...
                      </Text>
                    )}
                    {!loadingConfig && (!countries || countries.length === 0) && (
                      <Text variant="caption" color="error" style={styles.helperText}>
                        No countries available
                      </Text>
                    )}
                  </View>
                  <View style={styles.mobileNumberInput}>
                    <Text style={styles.label}>Mobile Number</Text>
                    <Input
                      placeholder="9876543210"
                      value={createForm.mobileNumber}
                      onChangeText={(value) => {
                        // Only allow digits and limit to 10 characters
                        const numericValue = value.replace(/\D/g, '').slice(0, 10);
                        setCreateForm((prev) => ({ ...prev, mobileNumber: numericValue }));
                        // Clear mobile number error when user types
                        if (createErrors.mobileNumber) {
                          setCreateErrors((prev) => ({ ...prev, mobileNumber: undefined }));
                        }
                      }}
                      keyboardType="phone-pad"
                      testID="create-user-mobilenumber-input"
                      error={createErrors.mobileNumber}
                      maxLength={10}
                    />
                    {createErrors.mobileNumber && (
                      <Text variant="caption" color="error" style={styles.errorText}>
                        {createErrors.mobileNumber}
                      </Text>
                    )}
                    {!createErrors.mobileNumber && createForm.mobileNumber.length > 0 && createForm.mobileNumber.length < 10 && (
                      <Text variant="caption" color="secondary" style={styles.helperText}>
                        {createForm.mobileNumber.length}/10 digits
                      </Text>
                    )}
                  </View>
                </View>

                <View style={styles.roleSelector}>
                  <Text style={styles.label}>Role</Text>
                  <View style={styles.roleDisplayContainer}>
                    <View style={styles.roleDisplay}>
                      <Text variant="body" bold style={styles.roleDisplayText}>
                        STAFF
                      </Text>
                      <Text variant="caption" color="secondary" style={styles.roleHint}>
                        (Only STAFF users can be created)
                      </Text>
                    </View>
                  </View>
                </View>

                <Input
                  label="Password"
                  placeholder="SecurePassword123!"
                  value={createForm.password}
                  onChangeText={(value) => {
                    setCreateForm((prev) => ({ ...prev, password: value }));
                    if (createErrors.password) setCreateErrors((prev) => ({ ...prev, password: undefined }));
                  }}
                  secureTextEntry
                  error={createErrors.password}
                  testID="create-user-password-input"
                />

                <Input
                  label="Confirm Password"
                  placeholder="SecurePassword123!"
                  value={createForm.confirmPassword}
                  onChangeText={(value) => {
                    setCreateForm((prev) => ({ ...prev, confirmPassword: value }));
                    if (createErrors.confirmPassword)
                      setCreateErrors((prev) => ({ ...prev, confirmPassword: undefined }));
                  }}
                  secureTextEntry
                  error={createErrors.confirmPassword}
                  testID="create-user-confirm-password-input"
                />

                <View style={styles.modalActions}>
                  <Button
                    variant="secondary"
                    onPress={() => setShowAddUser(false)}
                    disabled={saving}
                    style={styles.modalActionButton}
                  >
                    Cancel
                  </Button>
                  <Button
                    onPress={handleCreateUser}
                    disabled={saving}
                    loading={saving}
                    style={styles.modalActionButton}
                    themeColor={accentColor}
                    testID="create-user-submit-button"
                  >
                    {saving ? 'Creating...' : 'Create User'}
                  </Button>
                </View>
              </Card>
            </ScrollView>
          </View>
        </Modal>
      )}

      {/* Reset Password Modal */}
      <Modal
        visible={showResetPassword !== null}
        transparent
        animationType="slide"
        onRequestClose={() => setShowResetPassword(null)}
      >
        <View style={[styles.modalContainer, { backgroundColor: 'rgba(0, 0, 0, 0.5)' }]}>
          <ScrollView contentContainerStyle={[styles.modalContent, { backgroundColor: themeColors.secondary }]} keyboardShouldPersistTaps="handled">
            <View style={[styles.modalHeader, { backgroundColor: themeColors.primary }]}>
              <Text variant="h3" bold style={{ color: colors.white }}>
                🔑 Reset Password
              </Text>
              <TouchableOpacity onPress={() => setShowResetPassword(null)}>
                <Text style={[styles.closeButton, { color: colors.white }]}>✕</Text>
              </TouchableOpacity>
            </View>

            <Card style={styles.formCard}>
              <Text variant="body" color="secondary" style={styles.resetPasswordInfo}>
                Enter the new password for this user
              </Text>

              <Input
                label="New Password"
                placeholder="SecurePassword123!"
                value={resetPasswordForm.password}
                onChangeText={(value) => {
                  setResetPasswordForm((prev) => ({ ...prev, password: value }));
                  if (resetPasswordErrors.password)
                    setResetPasswordErrors((prev) => ({ ...prev, password: undefined }));
                }}
                secureTextEntry
                error={resetPasswordErrors.password}
                testID="reset-password-input"
              />

              <Input
                label="Confirm New Password"
                placeholder="SecurePassword123!"
                value={resetPasswordForm.confirmPassword}
                onChangeText={(value) => {
                  setResetPasswordForm((prev) => ({ ...prev, confirmPassword: value }));
                  if (resetPasswordErrors.confirmPassword)
                    setResetPasswordErrors((prev) => ({
                      ...prev,
                      confirmPassword: undefined,
                    }));
                }}
                secureTextEntry
                error={resetPasswordErrors.confirmPassword}
                testID="reset-password-confirm-input"
              />

              <View style={styles.modalActions}>
                <Button
                  variant="secondary"
                  onPress={() => setShowResetPassword(null)}
                  disabled={saving}
                  style={styles.modalActionButton}
                >
                  Cancel
                </Button>
                <Button
                  onPress={() => {
                    if (showResetPassword) {
                      handleResetPassword(showResetPassword);
                    }
                  }}
                  disabled={saving}
                  loading={saving}
                  style={styles.modalActionButton}
                  themeColor={accentColor}
                  testID="reset-password-submit-button"
                >
                  {saving ? 'Resetting...' : 'Reset Password'}
                </Button>
              </View>
            </Card>
          </ScrollView>
        </View>
      </Modal>
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    flexGrow: 1,
    paddingVertical: 0,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 16,
  },
  notificationCard: {
    marginHorizontal: 16,
    marginTop: 16,
    marginBottom: 12,
    backgroundColor: '#E3F2FD',
    borderLeftWidth: 4,
    borderLeftColor: '#2196F3',
  },
  notificationContent: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  notificationIcon: {
    fontSize: 20,
    marginRight: 12,
  },
  notificationTextContainer: {
    flex: 1,
  },
  notificationText: {
    color: '#1565C0',
    marginBottom: 4,
  },
  notificationBold: {
    fontWeight: 'bold',
    color: '#0D47A1',
  },
  notificationSubtext: {
    marginTop: 4,
  },
  header: {
    marginBottom: 24,
    paddingHorizontal: 16,
    paddingTop: 16,
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
  addButton: {
    paddingVertical: 8,
    paddingHorizontal: 12,
  },
  usersList: {
    marginBottom: 24,
    paddingHorizontal: 16,
  },
  userCard: {
    marginBottom: 12,
  },
  userInfo: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  userDetails: {
    flex: 1,
  },
  userIdentifier: {
    marginBottom: 4,
  },
  userMeta: {
    flexDirection: 'row',
    gap: 12,
  },
  metaItem: {
    marginLeft: 12,
  },
  actions: {
    flexDirection: 'row',
    gap: 8,
  },
  actionButton: {
    flex: 1,
    paddingVertical: 8,
  },
  emptyCard: {
    padding: 32,
    alignItems: 'center',
  },
  emptyTitle: {
    marginBottom: 8,
  },
  emptyText: {
    textAlign: 'center',
  },
  restrictedCard: {
    padding: 32,
    alignItems: 'center',
    margin: 16,
  },
  restrictedTitle: {
    marginBottom: 16,
    color: colors.orange,
  },
  restrictedText: {
    textAlign: 'center',
    marginBottom: 12,
  },
  restrictedSubtext: {
    textAlign: 'center',
    marginBottom: 8,
  },
  modalContainer: {
    flex: 1,
  },
  modalContent: {
    flexGrow: 1,
    marginTop: 60,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    padding: 16,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 16,
    marginHorizontal: -16,
    marginTop: -16,
    marginBottom: 24,
  },
  closeButton: {
    fontSize: 28,
    fontWeight: 'bold',
  },
  formCard: {
    marginBottom: 24,
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.gray9,
    marginBottom: 4,
  },
  mobileInputGroup: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 16,
  },
  countryCodeInput: {
    flex: 1,
  },
  mobileNumberInput: {
    flex: 2,
  },
  roleSelector: {
    marginBottom: 16,
  },
  roleDisplayContainer: {
    marginTop: 4,
  },
  roleDisplay: {
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    backgroundColor: colors.gray1,
    borderWidth: 1,
    borderColor: colors.gray3,
  },
  roleDisplayText: {
    color: colors.gray6,
    fontSize: 16,
    marginBottom: 4,
  },
  roleHint: {
    fontSize: 12,
    fontStyle: 'italic',
  },
  modalActions: {
    flexDirection: 'row',
    gap: 12,
  },
  modalActionButton: {
    flex: 1,
  },
  resetPasswordInfo: {
    marginBottom: 16,
    textAlign: 'center',
  },
  // Country dropdown styles
  pickerContainer: {
    borderWidth: 1,
    borderColor: colors.gray3,
    borderRadius: 8,
    backgroundColor: colors.white,
    minHeight: 48,
    justifyContent: 'center',
    position: 'relative',
  },
  picker: {
    width: '100%',
    height: 48,
    paddingHorizontal: 12,
    paddingVertical: 0,
    fontSize: 14,
    color: colors.gray9,
    backgroundColor: 'transparent',
    borderWidth: 0,
    borderRadius: 8,
    fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
  } as any,
  helperText: {
    marginTop: 6,
    marginBottom: 0,
    fontSize: 12,
    lineHeight: 16,
    paddingLeft: 2,
  },
  errorText: {
    marginTop: 6,
    marginBottom: 0,
    fontSize: 12,
    lineHeight: 16,
    paddingLeft: 2,
    color: colors.error,
  },
});

